import {
  lazy,
  Suspense,
  useEffect,
  useState
} from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
    Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';
import CmsLogin from './pages/cms/CmsLogin';
import CmsShell from './pages/cms/CmsShell';
import InstructorApplication from './pages/cms/InstructorApplication';
import StudentApp from './pages/student/StudentApp';
import { getCmsSession } from './utils/auth';
import Voting from './pages/cms/Voting';
import VotingDisplay from './pages/student/VotingDisplay.js';
import UnifiedLogin from './UnifiedLogin';
import {
  PushNotifications
} from '@capacitor/push-notifications';
import {
  isNativeApp
} from './utils/nativeApp';

const EmbeddedInstructorApp = lazy(
  () => import(
    './instructor-app/EmbeddedInstructorApp'
  )
);

function BrowserBranding() {
  const location = useLocation();

  useEffect(() => {
    const instructorSide =
      location.pathname.startsWith('/cms') ||
      location.pathname.startsWith('/instructor') ||
      location.pathname === '/voting';

    const favicon =
      instructorSide
        ? '/instructor-favicon.png'
        : '/student-favicon.png';

    const title =
      'Tunghai Honors App';

    let link =
      document.querySelector(
        'link[rel~="icon"]'
      );

    if (!link) {
      link =
        document.createElement('link');

      link.rel = 'icon';

      document.head.appendChild(link);
    }

    link.type = 'image/png';
    link.href = favicon;

    document.title = title;
  }, [location.pathname]);

  return null;
}

const ACTIVE_ROLE_KEY =
  'veritasActiveRole';


function normalizedPushRole(
  value
) {
  const clean =
    String(
      value || ''
    )
      .trim()
      .toLowerCase();

  return [
    'student',
    'instructor'
  ].includes(clean)
    ? clean
    : null;
}


function resolveNativePushRole(
  data
) {
  const explicitRole =
    normalizedPushRole(
      data?.recipientRole ||
      data?.recipient_role ||
      data?.role
    );

  if (explicitRole) {
    return explicitRole;
  }


  const type =
    String(
      data?.type || ''
    )
      .trim()
      .toLowerCase();


  /*
   * These older Instructor payloads are
   * unambiguous even without recipientRole.
   */
  if (
    type ===
      'instructor_chat' ||
    type ===
      'instructor_morning_summary'
  ) {
    return 'instructor';
  }


  if (
    type ===
    'course_registration'
  ) {
    return 'student';
  }


  /*
   * instructor_meeting is historically
   * ambiguous: students also receive that
   * type for meetings WITH an instructor.
   *
   * honors_event is deliberately shared
   * between both roles.
   *
   * For older notifications that predate
   * recipientRole, preserve the user's
   * current/last role.
   */
  if (
    type ===
      'instructor_meeting' ||
    type ===
      'honors_event'
  ) {
    return (
      normalizedPushRole(
        localStorage.getItem(
          ACTIVE_ROLE_KEY
        )
      ) ||
      'student'
    );
  }


  /*
   * Existing Student pushes do not carry
   * recipientRole, so Student remains the
   * compatibility default.
   */
  return 'student';
}


function NativePushRouter() {
  const navigate =
    useNavigate();

  useEffect(() => {
    if (!isNativeApp()) {
      return undefined;
    }

    let listener = null;
    let cancelled = false;

    async function attach() {
      listener =
        await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          action => {
            if (cancelled) {
              return;
            }

            const data =
              action
                ?.notification
                ?.data ||
              {};

            const role =
              resolveNativePushRole(
                data
              );

            localStorage.setItem(
              ACTIVE_ROLE_KEY,
              role
            );

            navigate(
              '/' + role,
              {
                replace: true,

                state: {
                  nativePushTarget: {
                    ...data,

                    recipientRole:
                      role,

                    receivedAt:
                      Date.now()
                  }
                }
              }
            );
          }
        );
    }

    attach().catch(error => {
      console.error(
        'Could not attach unified push-action listener:',
        error
      );
    });

    return () => {
      cancelled = true;

      if (listener) {
        listener.remove();
      }
    };
  }, [
    navigate
  ]);

  return null;
}


function App() {
  const [cmsSession, setCmsSession] =
    useState(getCmsSession());

  return (
<BrowserRouter>
  <BrowserBranding />
  <NativePushRouter />

  <Routes>
        <Route
          path="/"
          element={<UnifiedLogin />}
        />

        <Route
          path="/cms/apply"
          element={<InstructorApplication />}
        />

        <Route
          path="/cms/*"
          element={
            cmsSession
              ? (
                  <CmsShell
                    session={cmsSession}
                    onLogout={() =>
                      setCmsSession(null)
                    }
                  />
                )
              : (
                  <CmsLogin
                    onLogin={setCmsSession}
                  />
                )
          }
        />

        <Route
          path="/student/*"
          element={<StudentApp />}
        />

        <Route
          path="/instructor/*"
          element={
            <Suspense fallback={null}>
              <EmbeddedInstructorApp />
            </Suspense>
          }
        />

        <Route
          path="/voting"
          element={
            cmsSession
              ? (
                  <Voting
                    session={cmsSession}
                  />
                )
              : (
                  <Navigate
                    to="/cms"
                    replace
                  />
                )
          }
        />

        <Route
          path="/display"
          element={<VotingDisplay />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;